import React, { useState, useEffect } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import CustomSelect from '../../../components/Entry/CustomSelect'
import ProvinceService from '../../provinces/services/Provinces'
import AgroDashboardService from '../service/AgroDashboard'
import DataSetsService from '../../datasets/services/DataSets'
import Controls from '../../../components/Entry/Controls'

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white px-4 py-3 rounded-xl shadow-lg border border-emerald-100">
                <p className="text-xs text-gray-400 font-medium mb-1">Année {label}</p>
                <p className="text-sm text-blue-600">
                    Surface globale (cumul) :{' '}
                    <span className="font-semibold">{payload[0]?.value?.toLocaleString('fr-FR')} m²</span>
                </p>
                <p className="text-sm text-emerald-600 mt-1">
                    Surface plantée (cumul) :{' '}
                    <span className="font-semibold">{payload[1]?.value?.toLocaleString('fr-FR')} m²</span>
                </p>
            </div>
        )
    }
    return null
}

const DEFAULT_VALUES = {
    site: '',
    year: new Date().getFullYear(),
    project_type: ''
}

export default function PlotsOverviewDashboard() {
    const [site, setSite] = useState([])
    const [projectTypes, setProjectTypes] = useState([]) // <-- options du select "Type de projet"
    const [values, setValues] = useState({ ...DEFAULT_VALUES })
    const [plotsCount, setPlotsCount] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        total_surface: 0,
        planted_surface: 0,
        available_surface: 0
    })
    const [surfaceData, setSurfaceData] = useState([])

    useEffect(() => {
        ProvinceService.getAgroSites().then((response) => {
            if (response?.data) {
                setSite(response?.data)
            }
        })
    }, [])

    useEffect(() => {
        DataSetsService.getDataCode('project_type')
            .then((res) => {
                const raw = res?.data?.[0]?.database

                if (!raw) {
                    setProjectTypes([])
                    return
                }

                try {
                    const parsed = JSON.parse(raw)
                    setProjectTypes(Array.isArray(parsed) ? parsed : [])
                } catch (e) {
                    console.error('Erreur parsing project_type database:', e)
                    setProjectTypes([])
                }
            })
            .catch((err) => console.error('Erreur chargement project_type:', err))
    }, [])

    useEffect(() => {
        AgroDashboardService.getPlotsCount({
            village: values.site || undefined,
            year: values.year
        })
            .then((data) => {
                if (data) {
                    setPlotsCount(
                        data || {
                            total: 0,
                            active: 0,
                            inactive: 0,
                            total_surface: 0,
                            planted_surface: 0,
                            available_surface: 0
                        }
                    )
                }
            })
            .catch((error) => {
                console.error("Une erreur s'est produite lors de la récupération du nombre de parcelles :", error)
            })
    }, [values.site, values.year])

    useEffect(() => {
        AgroDashboardService.getPlotsSurfaceCumulative({
            village: values.site || undefined
        })
            .then((data) => {
                if (data) {
                    setSurfaceData(data || [])
                }
            })
            .catch((error) => {
                console.error("Une erreur s'est produite lors de la récupération de la surface cumulative :", error)
            })
    }, [values.site])

    const getYears = () => {
        const currentYear = new Date().getFullYear()
        const options = []
        for (let i = currentYear - 4; i <= currentYear + 4; i++) {
            options.push(i)
        }
        return options
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        setValues((prevValues) => ({
            ...prevValues,
            [name]: value
        }))
    }

    // ── handleReset ───────────────────────────────────────────────
    // Vide tous les filtres (site, type de projet) ; seule l'année reste
    // à sa valeur par défaut (année courante).
    const handleReset = () => {
        setValues({ ...DEFAULT_VALUES })
    }

    const formatNumber = (number) => number?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')

    return (
        <div className="w-full bg-white p-4 rounded-xl border border-gray-200 flex flex-col mt-[20px]">
            <div className="flex justify-between">
                <div>
                    <p className="font-semibold text-lg">Parcelles</p>
                </div>
                <div>
                    <select
                        className="bg-[#0FCB8F] w-24 h-8 rounded-md border-none text-white"
                        name="year"
                        id="year"
                        value={values.year}
                        onChange={handleChange}
                    >
                        {getYears().map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="flex items-end gap-[40px] mb-[15px] mt-[16px]">
                <div className="w-full">
                    <Controls.AutoCompleteSelect
                        label="Site"
                        options={site}
                        onChange={handleChange}
                        name="site"
                        value={values.site}
                        valueKey="id"
                        labelKey="name"
                    />
                </div>
                {/* <div className="w-full">
                    <Controls.AutoCompleteSelect
                        label="Type de reboisement"
                        name="project_type"
                        value={values.project_type || ''}
                        onChange={handleChange}
                        options={projectTypes}
                        labelKey="label"
                        valueKey="label"
                    />
                </div> */}
                <button
                    type="button"
                    onClick={handleReset}
                    className="shrink-0 h-10 px-4 rounded-md border border-[#0FCB8F] text-[#0FCB8F] bg-white hover:bg-[#0FCB8F] hover:text-white transition-colors"
                >
                    Réinitialiser
                </button>
            </div>

            <div className="flex gap-[20px] mb-[10px]">
                <div className="flex-1 bg-emerald-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500">Total parcelles</p>
                    <p className="text-2xl font-bold text-emerald-700">
                        {formatNumber(plotsCount.total)}{' '}
                        <span className="text-sm font-bold text-emerald-700">
                            ({formatNumber(plotsCount?.surface?.total)} m²)
                        </span>
                    </p>
                </div>
                <div className="flex-1 bg-green-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500">Parcelles actives</p>
                    <p className="text-2xl font-bold text-green-600">
                        {formatNumber(plotsCount.active)}{' '}
                        <span className="text-sm font-bold text-green-600">
                            ({formatNumber(plotsCount?.surface?.active)} m²)
                        </span>
                    </p>
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500">Parcelles inactives</p>
                    <p className="text-2xl font-bold text-gray-500">
                        {formatNumber(plotsCount.inactive)}{' '}
                        <span className="text-sm font-bold text-gray-500">
                            ({formatNumber(plotsCount?.surface?.inactive)} m²)
                        </span>
                    </p>
                </div>
            </div>

            <div className="mt-[20px]">
                <p className="text-sm font-semibold text-gray-700 mb-6">
                    Superficie cumulative des parcelles (globale vs plantée)
                </p>
                <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={surfaceData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorTotalSurface" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                            </linearGradient>
                            <linearGradient id="colorPlantedSurface" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis
                            dataKey="year"
                            stroke="#9ca3af"
                            tick={{ fill: '#6b7280', fontSize: 13 }}
                            axisLine={{ stroke: '#e5e7eb' }}
                            tickLine={false}
                        />
                        <YAxis
                            stroke="#9ca3af"
                            tick={{ fill: '#6b7280', fontSize: 12 }}
                            axisLine={false}
                            tickLine={false}
                            width={55}
                        />
                        <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '4 4' }}
                        />
                        <Legend />
                        <Area
                            type="monotone"
                            dataKey="cumulative_total_surface"
                            stroke="#3b82f6"
                            strokeWidth={3}
                            fill="url(#colorTotalSurface)"
                            name="Surface globale (cumul)"
                            dot={{ fill: '#3b82f6', r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                        <Area
                            type="monotone"
                            dataKey="cumulative_planted_surface"
                            stroke="#059669"
                            strokeWidth={3}
                            fill="url(#colorPlantedSurface)"
                            name="Surface plantée (cumul)"
                            dot={{ fill: '#059669', r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}
